import { FunctionComponent, useMemo } from 'react';
import { AuthQueryParamValues, getAuthUrl } from 'utils/url-generator';
import {
  CommunityApp,
  CommunityNavigation,
  LoggedInUser,
} from '@atlassian/learning-components';
import useLoggedInUser from 'utils/useLoggedInUser';
import useIsLoggedIn from 'utils/useIsLoggedIn';
import {
  CommunityAnalyticsClient,
  eventType,
  getCommunityLoginUrl,
  getCommunityLogoutUrl,
  getCommunityIdentityLoginUrl,
  getCommunityIdentityLogoutUrl,
} from '@atlassian/community-library';
import {
  accountSwitcherIsActive,
  hlvVanillaMigrationIsActive,
  newNavigationIsActive,
} from 'services/statsig/featureGates';
import { ENVIRONMENT, getEnvironment } from 'utils/environment';

function getLoginUrl(
  queryParamValues: AuthQueryParamValues,
  env?: ENVIRONMENT,
) {
  if (hlvVanillaMigrationIsActive()) {
    return getCommunityIdentityLoginUrl(queryParamValues, env);
  }
  return getCommunityLoginUrl(queryParamValues, env);
}

function getLogoutUrl(
  queryParamValues: AuthQueryParamValues,
  env?: ENVIRONMENT,
) {
  if (hlvVanillaMigrationIsActive()) {
    return getCommunityIdentityLogoutUrl(queryParamValues, env);
  }
  return getCommunityLogoutUrl(queryParamValues, env);
}

export const NavBar: FunctionComponent = () => {
  const currentUrl = globalThis?.location?.href ?? window?.location?.href ?? '';
  const communityUrl = `${process.env.REACT_APP_COMMUNITY_BASE_URL}`;

  let updatedUrl;
  // temporary fix: conx-700
  if (currentUrl.includes('/settings')) {
    const url = new URL(currentUrl);
    url.searchParams.delete('errorCode');
    updatedUrl = `${url.origin}${url.pathname}${url.search}`;
  } else {
    updatedUrl = currentUrl;
  }

  const currentPage = encodeURIComponent(updatedUrl);
  const { accountId, name, picture, email } = useLoggedInUser() || {};
  const isLoggedIn = useIsLoggedIn();
  const normalizedUser: LoggedInUser = {
    id: accountId || '',
    publicName: name || '',
    avatar: picture,
  };

  const loginHref = useMemo(
    () =>
      getLoginUrl(
        {
          experience: 'entry-auth',
          email: email || '',
          redirectUrl: currentPage,
        },
        getEnvironment() as ENVIRONMENT,
      ),
    [currentPage],
  );
  const logoutHref = useMemo(() => {
    // CONX-892: When a user logs out from settings page, redirect them to the home page
    const redirectUrl = decodeURIComponent(currentPage).endsWith(
      '/user/settings',
    )
      ? encodeURIComponent(communityUrl)
      : currentPage;
    return getLogoutUrl(
      {
        experience: 'entry-auth',
        email: email || '',
        redirectUrl,
      },
      getEnvironment() as ENVIRONMENT,
    );
  }, [currentPage]);
  const signupHref = useMemo(
    () =>
      getAuthUrl('signup', {
        experience: 'entry-auth',
        email: email || '',
        redirectUrl: currentPage,
      }),
    [currentPage],
  );
  const switchHref = useMemo(
    () =>
      accountSwitcherIsActive()
        ? getLoginUrl(
            {
              redirectUrl: currentPage,
              experience: 'entry-auth',
              prompt: 'select_account',
            },
            getEnvironment() as ENVIRONMENT,
          )
        : '',
    [currentPage],
  );

  return (
    <>
      <CommunityNavigation
        currentApp={CommunityApp.PROFILE}
        isLoggedIn={isLoggedIn}
        user={normalizedUser}
        loginHref={loginHref}
        logoutHref={logoutHref}
        signupHref={signupHref}
        switchHref={switchHref}
        showNewMenu={newNavigationIsActive()}
        onAnalyticsSendEvent={(type, payload) =>
          CommunityAnalyticsClient.sendEvent(
            type as unknown as eventType,
            payload,
          )
        }
        email={email || ''}
      />
    </>
  );
};
